import React, { useRef } from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Box,
  CardMedia,
  useTheme,
  useMediaQuery
} from '@mui/material';
import AboutUs from './AboutUs';
import ContactUs from './ContactUs';
import { motion } from 'framer-motion';
import Logo from '../assets/logo.svg';
import aiSolutionsImage from '../assets/AISolutions.svg';
import cloudMigration from '../assets/CloudMigration.svg';
import desktopAppDevelopment from '../assets/DesktopApp.svg';
import digitalMarketing from '../assets/DigitalMarketing.svg';
import eCommerce from '../assets/Ecommerce.svg';
import SEO from '../assets/SEO.svg';
import websiteDevelopment from '../assets/Website.svg';
import mobileAppDevelopment from '../assets/MobileApp.svg';
import TrueFocus from './Animations/TrueFocus';
import SplitText from './Animations/SplitText';
import GradientText from './Animations/GradientText';
import { Element } from 'react-scroll'; // Import Element from react-scroll

const services = [
  {
    title: 'AI Solutions',
    description:
      'We provide cutting-edge AI solutions that help businesses harness the power of artificial intelligence to improve operations, enhance customer experiences, and drive innovation. Our services include machine learning models, natural language processing, computer vision, and data analytics, all tailored to meet your specific business objectives. Let us help you unlock the potential of AI for your company.',
    src: aiSolutionsImage // Added image path
  },
  {
    title: 'Website Development',
    description:
      'We specialize in creating responsive, user-friendly websites that are tailored to your business needs. Our team utilizes the latest technologies and design principles to ensure your website is not only visually appealing but also optimized for performance and user engagement. Whether you need a simple landing page or a complex web application, we’ve got you covered.',
    src: websiteDevelopment // Added image path
  },
  {
    title: 'E-commerce Solutions',
    description:
      'Our e-commerce solutions provide a comprehensive platform for your online store. From product listing to payment processing, we ensure a seamless shopping experience for your customers. Our solutions are designed with security in mind, ensuring that your customers’ data is protected while providing advanced features like inventory management and customer analytics.',
    src: eCommerce // Added image path
  },
  {
    title: 'Mobile App Development',
    description:
      'In today’s mobile-first world, having a robust mobile application is crucial. We develop high-performance mobile apps for both iOS and Android platforms, ensuring a native look and feel. Our development process focuses on user experience, security, and scalability, making sure your app can grow as your business does.',
    src: mobileAppDevelopment // Added image path
  },
  {
    title: 'Desktop App Development',
    description:
      'We offer custom desktop application development to streamline your business processes. Whether it’s a data management tool or a specialized software solution, our team ensures that your application meets your specific requirements. We leverage the latest technologies to create robust, secure, and efficient desktop applications.',
    src: desktopAppDevelopment // Added image path
  },
  {
    title: 'Cloud Migration',
    description:
      'Migrating to the cloud can enhance your business’s efficiency and scalability. Our cloud migration services ensure a smooth transition with minimal disruption to your operations. We assess your existing infrastructure, plan the migration process, and support you through the transition, ensuring that your data and applications remain secure throughout.',
    src: cloudMigration // Added image path
  },
  {
    title: 'SEO Services',
    description:
      'Our SEO experts employ proven strategies to improve your website’s visibility on search engines. We conduct comprehensive keyword research, optimize on-page elements, and build high-quality backlinks to help you rank higher in search results. With our SEO services, you can expect increased organic traffic and better ROI.',
    src: SEO // Added image path
  },
  {
    title: 'Digital Marketing',
    description:
      'We provide a full suite of digital marketing services to help you reach your target audience effectively. From social media management to email marketing and content creation, our strategies are tailored to drive engagement and conversions. Let us help you build a strong online presence and connect with your customers in meaningful ways.',
    src: digitalMarketing // Added image path
  }
];

const HomePage = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const overlayRef = useRef(null);

  return (
    <Container style={{ marginTop: '0rem' }}>
      {/* First Animation - Fade Out Logo */}
      <motion.div
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: 5 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999 // Temporary z-index for animation
        }}
        ref={overlayRef} // Attach ref here
        onAnimationComplete={() => {
          // Hide the overlay by modifying its style after the animation is complete
          if (overlayRef.current) {
            overlayRef.current.style.display = 'none'; // Hides the overlay
          }
        }}
      >
        <motion.img
          src={Logo}
          alt='Logo'
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 3 }}
          style={{ width: '200px', height: '200px' }}
        />
      </motion.div>
<Element name="homeSection">
      <Box
        sx={{
          display: 'flex',
          marginBottom: 5,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 2
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: { xs: '80%', sm: '40%' }
          }}
        >
          <TrueFocus
            sentence="Goatified Coding Simplified Solutions"
            manualMode={false}
            blurAmount={5}
            borderColor="#ffeb3b"
            animationDuration={2}
            pauseBetweenAnimations={0.5}
          />
        </div>
        <div
          style={{
            width: '100%',
            maxWidth: { xs: '80%', sm: '40%' }
          }}
        >
          <SplitText
            text="We empower SMB businesses with cutting-edge software solutions. Our
            expertise lies in delivering innovative, scalable, and customized
            applications that drive growth and efficiency."
            className="text-2xl font-semibold text-center"
            delay={150}
            animationFrom={{ opacity: 0, transform: 'translate3d(0,50px,0)' }}
            animationTo={{ opacity: 1, transform: 'translate3d(0,0,0)' }}
            easing="easeOutCubic"
            threshold={0.2}
            rootMargin="-50px"
          />
        </div>
      </Box>
      </Element>
      <GradientText
        colors={["#ffffff", "#ffeb3b", "#ffffff", "#ffeb3b", "#ffffff"]}
        animationSpeed={3}
        showBorder={true}
        className="custom-class"
      >
        Our Services & Offerings
      </GradientText>

      <Element name="servicesSection">
        <Container>
          {services.map(service => (
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Card
                key={service.title}
                style={{
                  display: 'flex',
                  marginTop: 30,
                  marginBottom: 30
                }}
              >
                <Box
                  display='flex'
                  flexDirection={isSmallScreen ? 'column' : 'row'}
                >
                  <CardMedia
                    component='img'
                    image={service.src}
                    alt={service.title}
                    sx={{
                      width: isSmallScreen ? '100%' : '300px',
                      height: '300px'
                    }}
                  />
                  <CardContent>
                    <Typography
                      variant='h5'
                      component='div'
                      sx={{ marginBottom: isSmallScreen ? 0 : 5 }}
                    >
                      {service.title}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {service.description}
                    </Typography>
                  </CardContent>
                </Box>
              </Card>
            </motion.div>
          ))}
        </Container>
      </Element>

      <Element name="aboutUsSection">
        <AboutUs />
      </Element>

      <Element name="contactUsSection">
        <ContactUs />
      </Element>
    </Container>
  );
};

export default HomePage;
